import { z } from 'zod';
import { TimestampMsSchema } from '../time';
import { LotIdSchema } from '../ids';
import { PowerIdSchema } from './reputation';

/*
 * The powers' immune system (NPC-KINGDOM-REACT, IM-1..4): a coalition of powers against a faction
 * that abused their people, told to its members step by step (IM-2) and never with its number.
 */

/** A coalition's steps, in order (IM-2). Ids are a public contract (wire, i18n): never rename. */
export const COALITION_STEP_IDS = ['embargo', 'bounty', 'incursion'] as const;
export const CoalitionStepSchema = z.enum(COALITION_STEP_IDS);

/** What a faction is shown of its threat (IK-8): never the number. */
export const ThreatLevelSchema = z.enum(['quiet', 'watched', 'near']);
export type ThreatLevelView = z.infer<typeof ThreatLevelSchema>;

/** The faction a coalition stands against: a company, or a character alone, with its name. */
export const CoalitionTargetSchema = z.object({
  kind: z.enum(['company', 'player']),
  id: z.string().min(1).max(64),
  name: z.string().min(1).max(64),
});

/** A coalition standing against the receiver's faction. */
export const CoalitionViewSchema = z.object({
  id: z.string().min(1).max(64),
  target: CoalitionTargetSchema,
  powers: z.array(PowerIdSchema).max(32),
  step: CoalitionStepSchema,
  since: TimestampMsSchema,
  /** When its last step came (game time). */
  steppedAt: TimestampMsSchema,
  level: ThreatLevelSchema,
  /**
   * Its incursions under way against the faction's lots (IM-2.3, NI-6): which lot, and the game
   * time its window ends. Absent: none.
   */
  incursions: z
    .array(z.object({ lot: LotIdSchema, until: TimestampMsSchema }))
    .max(32)
    .optional(),
});
export type CoalitionView = z.infer<typeof CoalitionViewSchema>;

/** The receiver's faction's standing with the powers: its coalition, if any, and its level. */
export const CoalitionStateSchema = z.object({
  coalition: CoalitionViewSchema.nullable(),
  level: ThreatLevelSchema,
});
export type CoalitionState = z.infer<typeof CoalitionStateSchema>;
